import React from 'react'
import { RotateCcw, Heart, Baby, TrendingUp, Info } from 'lucide-react'
import GenderIcon from './GenderIcon'
import CrystalBall from './CrystalBall'
import AngelWings from './AngelWings'
import StorkDelivery from './StorkDelivery'
import MysticalPortal from './MysticalPortal'
import './AnalysisResults.css'

interface AnalysisResult {
  prediction: 'boy' | 'girl' | null
  confidence: number
  analysis: string
  normalizedImageBase64?: string
  nubRegionBase64?: string
  detectedSpineAngle?: number
  detectedNubAngle?: number
}

interface AnalysisResultsProps {
  result: AnalysisResult
  onReset: () => void
}

const AnalysisResults: React.FC<AnalysisResultsProps> = ({ result, onReset }) => {
  const { prediction, confidence, analysis, normalizedImageBase64, nubRegionBase64, detectedSpineAngle, detectedNubAngle } = result

  const getGenderColor = () => {
    return prediction === 'boy' ? '#3b82f6' : '#ec4899'
  }

  const getConfidenceLevel = () => {
    if (confidence >= 90) return 'Very High'
    if (confidence >= 80) return 'High'
    if (confidence >= 70) return 'Good'
    return 'Moderate'
  }

  const getConfidenceColor = () => {
    if (confidence >= 90) return '#059669'
    if (confidence >= 80) return '#0891b2'
    if (confidence >= 70) return '#ea580c'
    return '#dc2626'
  }
  return (
    <div className="results-section">
      {/* Magical Background Elements */}
      <div className="magical-background">
        <div className="background-crystal">
          <CrystalBall size={80} animated={true} glowing={true} />
        </div>
        <div className="background-wings">
          <AngelWings 
            size={60} 
            animated={true} 
            color={prediction === 'boy' ? 'blue' : 'pink'} 
          />
        </div>
        <div className="background-portal">
          <MysticalPortal size={100} animated={true} gender={prediction!} />
        </div>
      </div>

      <div className="results-header">
        <div className="prediction-card" style={{ borderColor: getGenderColor() }}>
          <div className="gender-icon-container">
            <div className="main-icon">
              <GenderIcon gender={prediction!} size={120} animated={true} />
            </div>
            <div className="stork-delivery">
              <StorkDelivery size={80} animated={true} gender={prediction!} />
            </div>
          </div>
          <h2 className="prediction-title">
            It's a <span style={{ color: getGenderColor() }}>{prediction}!</span>
          </h2>
          <div className="confidence-badge" style={{ backgroundColor: getConfidenceColor() }}>
            <TrendingUp size={16} />
            {confidence}% confidence ({getConfidenceLevel()})
          </div>
        </div>
      </div>

      <div className="analysis-details">
        <div className="analysis-card">
          <div className="card-header">
            <Info className="card-icon" />
            <h3>Analysis Details</h3>
          </div>
          <p className="analysis-text">{analysis}</p>
        </div>

        <div className="nub-theory-info">
          <div className="card-header">
            <Baby className="card-icon" />
            <h3>About Nub Theory</h3>
          </div>
          <p>
            The nub theory is based on the angle and shape of the genital tubercle visible in
            ultrasound scans between 11-14 weeks. Our AI analyzes these characteristics to
            make predictions, but remember that this is for entertainment purposes only.
          </p>
        </div>

        {/* Debug Visualization */}
        {(normalizedImageBase64 || nubRegionBase64) && (
          <div className="debug-visualization">
            <div className="card-header">
              <Info className="card-icon" />
              <h3>Analysis Visualization</h3>
            </div>
            <p style={{ fontSize: '0.9rem', color: '#666', marginBottom: '1rem' }}>
              These images show what our AI analyzed:
            </p>
            <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', justifyContent: 'center' }}>
              {normalizedImageBase64 && (
                <div style={{ textAlign: 'center' }}>
                  <p style={{ fontWeight: 'bold', marginBottom: '0.5rem' }}>
                    Normalized Image (Spine: {detectedSpineAngle?.toFixed(1)}°)
                  </p>
                  <img
                    src={normalizedImageBase64}
                    alt="Normalized ultrasound"
                    style={{ maxWidth: '200px', border: '2px solid #ddd', borderRadius: '8px' }}
                  />
                </div>
              )}
              {nubRegionBase64 && (
                <div style={{ textAlign: 'center' }}>
                  <p style={{ fontWeight: 'bold', marginBottom: '0.5rem' }}>
                    Nub Region (Angle: {detectedNubAngle?.toFixed(1)}°)
                  </p>
                  <img
                    src={nubRegionBase64}
                    alt="Nub region"
                    style={{ maxWidth: '200px', border: '2px solid #ddd', borderRadius: '8px' }}
                  />
                  <p style={{ fontSize: '0.8rem', color: '#888', marginTop: '0.5rem' }}>
                    {(detectedNubAngle ?? 0) > 30 ? '↑ Upward angle (Boy)' : '→ Horizontal angle (Girl)'}
                  </p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      <div className="important-notice">
        <div className="notice-content">
          <Heart className="notice-icon" />
          <div>
            <h4>Important Notice</h4>
            <p>
              This prediction is for entertainment purposes only and should not replace 
              professional medical advice. For accurate gender determination, consult 
              with your healthcare provider.
            </p>
          </div>
        </div>
      </div>

      <div className="action-buttons">
        <button className="try-again-button" onClick={onReset}>
          <RotateCcw size={20} />
          Try Another Scan
        </button>
        <button className="share-button">
          <Heart size={20} />
          Share Results
        </button>
      </div>
    </div>
  )
}

export default AnalysisResults
